<template>
  <div class="checkin-page glassy-app">
    <v-flex lg4 md4 xs12 sm12>
      <v-switch v-model="guestVerificationEnabled"
                label="Require Guest Verification"
                color="primary"
                class="modern-switch glassy-switch"
                :hint="guestVerificationEnabled ? 'Verification required before check-in' : 'Verification not required'"
                persistent-hint readonly></v-switch>
    </v-flex>
    <div class="checkin-container">
      <v-expansion-panel popout light class="modern-panel glassy-panel slide-in" flat>
        <v-expansion-panel-content class="modern-panel glassy-panel slide-in">
          <template slot="header">
            <h4 class="panel-title earth-color">EXPRESS CHECKIN</h4>
          </template>
          <template slot="actions">
            <v-icon color="primary" class="expand-icon">$vuetify.icons.expand</v-icon>
          </template>
          <v-layout row wrap>
            <v-container>
              <v-flex lg4 md4 v-if="singleBookingCheckIn.gatepass_guest_count === 0">
                <v-switch v-model="checkin.activity_check_in"
                          label="Activity Express Check In"
                          color="primary"
                          class="modern-switch glassy-switch"></v-switch>
              </v-flex>
              <v-flex lg4 md4 xs12 sm12 v-if="singleBookingCheckIn.destination_set === false">
                <v-autocomplete label="Destination"
                                v-model="checkin.destination"
                                :items="allDestinations"
                                :rules="[(v) => !!v || 'Item is required']"
                                required
                                item-text="destination_name"
                                item-value="destination_public_id"
                                :loading="$loadingTrue"
                                class="modern-autocomplete glassy-input"
                                v-if="singleBookingCheckIn.destination_set === false"></v-autocomplete>
              </v-flex>
            </v-container>
          </v-layout>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel popout light class="modern-panel glassy-panel slide-in-delayed" flat>
        <v-expansion-panel-content class="modern-panel glassy-panel slide-in">
          <template slot="header">
            <h4 class="panel-title earth-color">DRIVER DETAILS</h4>
          </template>
          <v-layout row wrap v-if="checkin.activity_check_in === false">
            <v-flex lg4 md4 sm12 xs12>
              <v-container>
                <v-select label="Select Entrance"
                          v-model="checkin.source"
                          :items="allGates"
                          :rules="[(v) => !!v || 'Entrance is required']"
                          required
                          item-text="gate_name"
                          item-value="gate_public_id"
                          :loading="$loadingTrue"
                          class="modern-select glassy-input"></v-select>
              </v-container>
            </v-flex>
            <v-flex lg4 md4 sm12 xs12>
              <v-container>
                <v-text-field label="Driver's Name"
                              v-model="details.name"
                              :rules="requiredRules"
                              required
                              class="modern-input glassy-input"></v-text-field>
              </v-container>
            </v-flex>
            <v-flex lg4 md4 sm12 xs12>
              <v-container>
                <v-text-field label="Driver's Phone Number"
                              v-model="details.phone_number"
                              :rules="requiredRules"
                              required
                              class="modern-input glassy-input"></v-text-field>
              </v-container>
            </v-flex>
            <v-flex lg4 md4 sm12 xs12>
              <v-container>
                <v-switch v-model="useDropdownVehicles"
                          label="Use dropdown for vehicles"
                          color="primary"
                          class="modern-switch glassy-switch" readonly></v-switch>
              </v-container>
            </v-flex>
            <template v-if="useDropdownVehicles">
              <v-flex lg4 md4 sm12 xs12>
                <v-container>
                  <v-autocomplete label="Select Registrations"
                                  v-model="selectedRegistrations"
                                  :items="unticketedRegItems"
                                  item-text="reg"
                                  item-value="reg"
                                  multiple
                                  chips
                                  deletable-chips
                                  filterable
                                  clearable
                                  dense
                                  hide-no-data
                                  :loading="$loadingTrue"
                                  placeholder="Search or select registrations"
                                  class="modern-autocomplete glassy-input">
                  </v-autocomplete>
                </v-container>
              </v-flex>
              <v-flex lg4 md4 sm12 xs12>
                <v-container>
                  <v-text-field label="Vehicle Registrations"
                                :value="selectedRegistrations.join(', ')"
                                :rules="requiredRules"
                                required
                                readonly
                                class="modern-input glassy-input readonly-input"></v-text-field>
                </v-container>
              </v-flex>
            </template>
            <template v-else>
              <v-flex lg4 md4 sm12 xs12>
                <v-container>
                  <v-text-field label="Vehicle Registrations (comma separated)"
                                v-model="manualRegistrations"
                                :rules="requiredRules"
                                required
                                class="modern-input glassy-input"
                                placeholder="e.g. ABC123, XYZ789"></v-text-field>
                </v-container>
              </v-flex>
            </template>
            <v-flex lg4 md4 sm12 xs12>
              <v-container>
                <v-text-field label="Vehicle Sensor Id"
                              v-model="details.vehicle_disk"
                              :rules="requiredRules"
                              required
                              class="modern-input glassy-input"></v-text-field>
              </v-container>
            </v-flex>
            <v-flex lg4 md4 sm12 xs12>
              <v-container>
                <v-btn :depressed="true"
                       color="primary"
                       block
                       @click="postDetails()"
                       class="modern-btn glassy-btn-primary">Add Details</v-btn>
              </v-container>
            </v-flex>
          </v-layout>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel popout light class="modern-panel glassy-panel slide-in-delayed" flat
                         v-model="expandedPanels"
                         v-if="guestVerificationEnabled && verifiableGuestTypes.length > 0">
        <v-expansion-panel-content class="modern-panel glassy-panel slide-in">
          <template slot="header">
            <h4 class="panel-title earth-color">VERIFY GUESTS</h4>
          </template>
          <v-layout row wrap>
            <v-flex xs12>
              <v-container>
                <p class="glassy-text">
                  <span v-if="verificationProgress">
                    <strong>{{ verificationProgress }}</strong>
                  </span>
                  <span v-else>
                    Please verify each guest individually before check-in.
                  </span>
                  <span v-if="allVerifiableGuestsVerified" class="green--text">All guests verified</span>
                </p>
                <v-btn v-if="!allVerifiableGuestsVerified && !verificationInProgress"
                       color="primary"
                       @click="startVerification()"
                       class="modern-btn glassy-btn-primary">
                  Start Verification
                </v-btn>
                <v-btn v-if="verificationInProgress"
                       color="warning"
                       @click="stopVerification()"
                       class="modern-btn glassy-btn-warning">
                  Stop Verification
                </v-btn>
              </v-container>
            </v-flex>

            <v-flex lg3 md4 sm6 xs12
                    v-for="(guest, key) in verifiableGuestTypes"
                    :key="key">
              <v-container fluid class="pa-1">
                <v-card class="compact-summary-card glassy-sub-card" flat>
                  <v-container fluid class="pt-2 pb-2">
                    <div class="summary-item">
                      <span class="arial-text glassy-text-dark text-uppercase">
                        {{ guest.payment_person }}
                      </span>
                      <span class="count-badge glassy-badge">
                        {{ guest.verifiedCount || 0 }}/{{ guest.payment_guests }}
                      </span>
                    </div>

                    <div class="doc-type-badge mt-1">
                      <v-chip small :color="getGuestDocTypeColor(guest)" variant="outlined">
                        <v-icon left small>mdi-{{ getGuestDocTypeIcon(guest) }}</v-icon>
                        {{ getGuestDocTypeLabel(guest) }}
                      </v-chip>
                    </div>

                    <v-progress-linear :value="(guest.verifiedCount || 0) / guest.payment_guests * 100"
                                       color="success"
                                       height="6"
                                       class="mt-1 glassy-progress"></v-progress-linear>

                    <div class="verification-status mt-2">
                      <v-chip v-if="guest.verifiedCount >= guest.payment_guests"
                              color="success"
                              small
                              text-color="white"
                              class="glassy-chip">
                        Verified
                      </v-chip>
                      <v-chip v-else-if="guest.verifiedCount > 0"
                              color="info"
                              small
                              text-color="white"
                              class="glassy-chip">
                        {{ guest.verifiedCount }} verified
                      </v-chip>
                      <v-chip v-else
                              color="warning"
                              small
                              text-color="white"
                              class="glassy-chip">
                        Not verified
                      </v-chip>
                    </div>

                    <v-btn @click="openScannerForGuest(guest, key)"
                           color="primary"
                           small
                           block
                           class="mt-2 glassy-btn-small"
                           :loading="guest.scanning"
                           :disabled="guest.verifiedCount >= guest.payment_guests">
                      <v-icon left small>mdi-camera</v-icon>
                      {{ guest.verifiedCount >= guest.payment_guests ? 'Done' : 'Verify Next' }}
                    </v-btn>

                    <div v-if="guest.lastProof" class="proof-text mt-1">
                      <small class="glassy-text-muted">
                        Proof: {{ guest.lastProof.substring(0, 12) }}...
                      </small>
                    </div>
                  </v-container>
                </v-card>
              </v-container>
            </v-flex>

            <v-flex xs12 v-if="exemptGuestTypes.length > 0">
              <v-container>
                <v-alert type="info" dense class="glassy-alert">
                  <strong>Exempt from verification:</strong>
                  <span v-for="(exempt, idx) in exemptGuestTypes" :key="idx">
                    {{ exempt.payment_person }} ({{ exempt.payment_guests }})<span v-if="idx < exemptGuestTypes.length-1">, </span>
                  </span>
                </v-alert>
              </v-container>
            </v-flex>
          </v-layout>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel popout light v-if="proofOfResidencyCheckIn.length > 0" class="modern-panel glassy-panel slide-in-delayed" flat>
        <v-expansion-panel-content class="modern-panel glassy-panel slide-in">
          <template slot="header">
            <h4 class="panel-title earth-color">PROOF OF RESIDENCY</h4>
          </template>
          <table cellpadding="1" cellspacing="1" class="table table-bordered modern-table glassy-table" v-if="proofOfResidencyCheckIn.length > 0">
            <tbody>
              <tr v-for="(residency, key) in proofOfResidencyCheckIn" :key="key">
                <td>
                  <v-text-field readonly
                                label="Residency"
                                v-model="proofOfResidencyCheckIn[key].payment_person"
                                class="modern-input glassy-input"></v-text-field>
                </td>
                <td>
                  <v-text-field readonly
                                label="Point Of Proof"
                                v-model="proofOfResidencyCheckIn[key].proof_point"
                                class="modern-input glassy-input"></v-text-field>
                </td>
                <td>
                  <v-text-field label="First Name"
                                v-model="proofOfResidencyCheckIn[key].first_name"
                                class="modern-input glassy-input"></v-text-field>
                </td>
                <td>
                  <v-text-field label="Last Name"
                                v-model="proofOfResidencyCheckIn[key].last_name"
                                class="modern-input glassy-input"></v-text-field>
                </td>
                <td>
                  <v-text-field label="Document Id"
                                v-model="proofOfResidencyCheckIn[key].document_id"
                                class="modern-input glassy-input"></v-text-field>
                </td>
                <td>
                  <v-switch v-model="proofOfResidencyCheckIn[key].verified"
                            label="Verify"
                            color="primary"
                            class="modern-switch glassy-switch"></v-switch>
                </td>
              </tr>
            </tbody>
            <v-btn :depressed="true"
                   color="info"
                   dark
                   class="white--text modern-btn glassy-btn-info"
                   small
                   slot="activator"
                   @click="booking_ref_proof = true">
              Use Booking Reference
            </v-btn>
          </table>
          <v-dialog v-model="booking_ref_proof" persistent max-width="600px" class="modern-dialog glassy-dialog">
            <v-card class="dialog-card glassy-modal">
              <v-card-title>
                <span class="headline">
                  <h4 class="earth-color dialog-title">BOOKING REF Number</h4>
                </span>
              </v-card-title>
              <v-card-text>
                <v-container grid-list-md>
                  <v-layout wrap>
                    <v-flex xs12 sm12 md12>
                      <v-text-field label="Booking ref"
                                    required
                                    v-model="proof_booking_id.booking_id"
                                    class="modern-input glassy-input"></v-text-field>
                    </v-flex>
                  </v-layout>
                </v-container>
                <div class="row" v-if="proofOfResidencyBookingId.length > 0">
                  <div class="col-md-12">
                    <h6 class="earth-color">Booking {{ proof_booking_id.booking_id }} Proofs</h6>
                    <table cellpadding="1" cellspacing="1" class="table table-bordered modern-table glassy-table">
                      <thead>
                        <tr>
                          <th>First Name</th>
                          <th>Last Name</th>
                          <th>Residency</th>
                          <th>Point of Proof</th>
                          <th>Document ID</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(proof_id, key) in proofOfResidencyBookingId" :key="key">
                          <td>{{ proof_id.first_name }}</td>
                          <td>{{ proof_id.last_name }}</td>
                          <td>{{ proof_id.payment_person }}</td>
                          <td>{{ proof_id.proof_point }}</td>
                          <td>{{ proof_id.document_id }}</td>
                          <td>
                            <v-btn :depressed="true" color="error" @click="removeProofBookingId(key)" class="modern-btn glassy-btn-danger">x</v-btn>
                          </td>
                        </tr>
                        <v-flex lg4 md4 sm12 xs12>
                          <v-btn color="primary" @click="confirmBookingProofsCheckin" class="modern-btn glassy-btn-primary">Use All</v-btn>
                        </v-flex>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="red darken-1" flat @click="closeBookingProofs()" class="modern-btn glassy-btn-text">Close</v-btn>
                <v-btn color="green darken-1" flat @click="getBookingProofs()" :loading="$loadingTrue" class="modern-btn glassy-btn-text">Search</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <br />

      <div class="info-section slide-in-delayed">
        <h4 class="section-title earth-color">Point Of Entry Details</h4>
        <v-container>
          <table cellpadding="1" cellspacing="1" class="table table-bordered modern-table glassy-table">
            <thead>
              <tr>
                <td><b>PAYMENT STATUS</b></td>
                <td v-if="singleBookingCheckIn.is_partner"><b>PAYMENT DETAILS</b></td>
                <td v-if="singleBookingCheckIn.entry_mode"><b>MODE OF ENTRY</b></td>
                <td v-if="singleBookingCheckIn.entry_mode"><b>POINT OF ENTRY</b></td>
                <td><b>MODE REGISTRATION DETAILS</b></td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ singleBookingCheckIn.booking_payment_status }}</td>
                <td v-if="singleBookingCheckIn.is_partner">{{ singleBookingCheckIn.paid_by }}</td>
                <td v-if="singleBookingCheckIn.entry_mode">{{ singleBookingCheckIn.entry_mode_name }}</td>
                <td v-if="singleBookingCheckIn.entry_mode">{{ singleBookingCheckIn.gate_name }}</td>
                <td>{{ singleBookingCheckIn.mode_registration }}</td>
              </tr>
            </tbody>
          </table>
        </v-container>
      </div>

      <div class="info-section slide-in-delayed" v-if="checkin.vehicles.length > 0">
        <h4 class="section-title earth-color">Driver Details</h4>
        <v-container>
          <table cellpadding="1" cellspacing="1" class="table table-bordered modern-table glassy-table">
            <thead>
              <tr>
                <td><b>DRIVER</b></td>
                <td><b>VEHICLE</b></td>
                <td><b>VEHICLE SENSOR ID</b></td>
                <td><b>ACTION</b></td>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(vehicle, key) in checkin.vehicles" :key="key">
                <td>{{ vehicle.driver_name }}</td>
                <td>{{ vehicle.reg_plate }}</td>
                <td>{{ vehicle.disk }}</td>
                <td>
                  <v-btn color="error" :depressed="true" small @click="removeVehicle(key)" class="modern-btn glassy-btn-danger">Delete</v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </v-container>
      </div>

      <div class="info-section slide-in-delayed" v-if="proofOfResidencyCheckIn.length > 0">
        <h4 class="section-title earth-color">Residency Proofs</h4>
        <v-container>
          <table cellpadding="1" cellspacing="1" class="table table-bordered modern-table glassy-table">
            <thead>
              <tr>
                <td><b>NAME</b></td>
                <td><b>POINT OF PROOF</b></td>
                <td><b>RESIDENCY</b></td>
                <td><b>DOCUMENT ID</b></td>
                <td><b>VERIFIED</b></td>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(proof, key) in proofOfResidencyCheckIn" :key="key">
                <td v-if="proof.first_name != '' && proof.last_name != '' && proof.document_id != '' && proof.verified != false">
                  {{ proof.first_name }} {{ proof.last_name }}
                </td>
                <td v-if="proof.first_name != '' && proof.last_name != '' && proof.document_id != '' && proof.verified != false">
                  {{ proof.proof_point }}
                </td>
                <td v-if="proof.first_name != '' && proof.last_name != '' && proof.document_id != '' && proof.verified != false">
                  {{ proof.payment_person }}
                </td>
                <td v-if="proof.first_name != '' && proof.last_name != '' && proof.document_id != '' && proof.verified != false">
                  {{ proof.document_id }}
                </td>
                <td v-if="proof.first_name != '' && proof.last_name != '' && proof.document_id != '' && proof.verified != false">
                  Yes
                </td>
              </tr>
            </tbody>
          </table>
        </v-container>
      </div>

      <v-btn v-if="(checkin.vehicles.length > 0 || checkin.activity_check_in === true)"
             :depressed="true"
             color="primary"
             @click="checkIn()"
             :loading="$loadingTrue"
             :disabled="!canCheckIn"
             class="modern-btn glassy-btn-complete slide-in-delayed">
        Complete Check In
      </v-btn>
      <div v-if="guestVerificationEnabled && verifiableGuestTypes.length > 0 && !allVerifiableGuestsVerified" class="mt-2">
        <v-alert type="warning" dense class="glassy-alert-warning">
          Please verify all guests before check-in.
        </v-alert>
      </div>

      <DocumentScanner v-model="scannerDialog"
                       :guest="currentGuest"
                       :guest-key="currentGuestKey"
                       @verified="handleVerification"
                       @close="onScannerClose" />
    </div>
  </div>
</template>

<script>
  import DocumentScanner from '@/components/DocumentScanner';

  export default {
    name: "check-in",
    components: {
      DocumentScanner
    },
    data() {
      return {
        guestVerificationEnabled: true,
        verificationPanelExpanded: true,
        requiredRules: [(v) => !!v || "This field is required"],
        count: false,
        details: {
          vehicle_disk: "0",
          name: "",
          phone_number: "254",
        },
        selectedRegistrations: [],
        manualRegistrations: "",
        useDropdownVehicles: true,
        checkin: {
          booking_id: this.$route.params.booking_id,
          session_id: localStorage.getItem("lkjhgfdsa"),
          vehicles: [],
          source: "",
          destination: "",
          activity_check_in: false,
        },
        proof: {
          first_name: "",
          last_name: "",
          residency: "",
          document_id: "",
          verified: false,
        },
        booking_ref_proof: false,
        proof_booking_id: {
          booking_id: "",
        },
        scannerDialog: false,
        currentGuest: null,
        currentGuestKey: null,
        verificationInProgress: false,
        verificationProgress: "",
        stopVerificationFlag: false,
        currentIndividualGuest: null,
        currentIndividualIndex: 0,
      };
    },
    beforeCreate() {
      this.$store.dispatch("get_full_conservation_fees");
      this.$store.dispatch("get_booking_checkin", this.$route.params.booking_id);
      this.$store.dispatch("get_all_destinations");
    },
    created() {
      this.$store.dispatch("get_booking", this.$route.params.booking_id);
      this.$store.dispatch("get_all_gates");
      this.$store.dispatch("get_unticketed_vehicles");
    },
    computed: {
      allGuests() {
        const booking = this.$store.state.BookingStore.booking;
        const bookingCheckin = this.$store.state.BookingStore.booking_checkin;
        let guests = [];
        if (booking && booking.guests) {
          guests = booking.guests;
        }
        let proofs = [];
        if (bookingCheckin && bookingCheckin.residency_proof) {
          proofs = bookingCheckin.residency_proof;
        }
        if (!guests.length) {
          return [];
        }

        return guests.filter(function (g) {
          return g.payment_guests > 0;
        }).map(function (g) {
          var verifiedCount = proofs.filter(function (p) {
            return p.payment_person === g.payment_person && p.verified === true;
          }).length;
          this.$set(g, 'verifiedCount', verifiedCount);
          this.$set(g, 'lastProof', null);

          return g;
        }.bind(this));
      },

      getGuestDocumentType() {
        return (guest) => {
          const name = guest.payment_person || '';
          const lower = name.toLowerCase();

          if (lower.includes('citizen')) return 'kenyan_id';
          if (lower.includes('ea resident')) return 'work_permit';
          if (lower.includes('non resident')) return 'passport';
          if (lower.includes('school driver')) return 'kenyan_id';
          if (lower.includes('resident')) return 'work_permit';
          return 'passport';
        };
      },

      getGuestDocTypeLabel() {
        return (guest) => {
          const type = this.getGuestDocumentType(guest);
          const labels = {
            'passport': 'Passport',
            'kenyan_id': 'Kenyan ID',
            'work_permit': 'Work Permit',
            'exempt': 'Exempt'
          };
          return labels[type] || 'Passport';
        };
      },

      getGuestDocTypeColor() {
        return (guest) => {
          const type = this.getGuestDocumentType(guest);
          const colors = {
            'passport': 'success',
            'kenyan_id': 'primary',
            'work_permit': 'info',
            'exempt': 'grey'
          };
          return colors[type] || 'success';
        };
      },

      getGuestDocTypeIcon() {
        return (guest) => {
          const type = this.getGuestDocumentType(guest);
          const icons = {
            'passport': 'passport',
            'kenyan_id': 'card-account-details',
            'work_permit': 'card-account-details',
            'exempt': 'check-circle'
          };
          return icons[type] || 'document';
        };
      },

verifiableGuestTypes() {
  const proofs = this.singleBookingCheckIn.residency_proof;
  if (!proofs || proofs.length === 0) return [];
  const names = proofs.map(p => p.payment_person);
  return this.allGuests.filter(g => names.includes(g.payment_person));
},

exemptGuestTypes() {
  const proofs = this.singleBookingCheckIn.residency_proof;
  if (!proofs || proofs.length === 0) return [];
  const names = proofs.map(p => p.payment_person);
  return this.allGuests.filter(g => !names.includes(g.payment_person));
},

      totalVerificationsNeeded() {
        return this.verifiableGuestTypes.reduce((sum, g) => sum + g.payment_guests, 0);
      },

      totalVerified() {
        return this.verifiableGuestTypes.reduce((sum, g) => sum + (g.verifiedCount || 0), 0);
      },

      allVerifiableGuestsVerified() {
        return this.totalVerified >= this.totalVerificationsNeeded;
      },

      isVerificationRequired() {
        return this.guestVerificationEnabled && this.verifiableGuestTypes.length > 0;
      },

      canCheckIn() {
        const hasVehicles = this.checkin.vehicles.length > 0;
        const isActivity = this.checkin.activity_check_in === true;
        if (!hasVehicles && !isActivity) return false;
        if (this.isVerificationRequired) {
          return this.allVerifiableGuestsVerified;
        }
        return true;
      },

      unticketedRegItems() {
        return this.$store.state.GateStore.unticketed_vehicles.map(v => ({ reg: v.vehicle_registration })) || [];
      },

      proofOfResidencyCheckInData: function () {
        let residency_proof_db = this.singleBookingCheckIn.residency_proof;
        if (residency_proof_db == undefined) {
          residency_proof_db = [];
        }
        this.$store.commit("CONSERVATION_FEES_PROOF_CHECKIN", residency_proof_db);
      },

      proofOfResidencyCheckIn: function () {
        return this.$store.state.ConservationFeeStore.conservation_fees_proof_checkin;
      },

      proofOfResidencyBookingId: function () {
        return this.$store.state.ConservationFeeStore.residency_proof_booking_id;
      },

      singleBookingCheckIn: function () {
        return this.$store.state.BookingStore.booking_checkin;
      }
    },
    methods: {
      showToast(message, type = 'info') {
        if (this.$toast && this.$toast[type]) {
          this.$toast[type](message);
        } else {
          if (type === 'error' || type === 'warning') {
            alert(message);
          }
        }
      },

      openScannerForGuest(guest, key) {
        if (!this.guestVerificationEnabled) {
          this.showToast('Guest verification is disabled. Enable it in the Express Checkin panel.', 'warning');
          return;
        }
        if (guest.verifiedCount >= guest.payment_guests) {
          this.showToast(`${guest.payment_person} already fully verified.`, 'info');
          return;
        }
        const index = guest.verifiedCount;
        this.currentIndividualGuest = guest;
        this.currentIndividualIndex = index;
        this.currentGuest = {
          ...guest,
          booking_id: this.checkin.booking_id
        };

        this.currentGuestKey = key;
        const progressMsg = `Verifying ${guest.payment_person} (${index + 1}/${guest.payment_guests})`;
        this.verificationProgress = progressMsg;
        this.showToast(progressMsg, 'info');
        this.scannerDialog = true;
      },

      startVerification() {
        if (!this.guestVerificationEnabled) {
          this.showToast('Guest verification is disabled. Enable it in the Express Checkin panel.', 'warning');
          return;
        }
        if (this.verificationInProgress) return;
        if (this.allVerifiableGuestsVerified) {
          this.showToast("All guests are already verified.", 'info');
          return;
        }
        this.verificationInProgress = true;
        this.stopVerificationFlag = false;
        this.verifyNextIndividual();
      },

      stopVerification() {
        this.verificationInProgress = false;
        this.stopVerificationFlag = true;
        this.verificationProgress = "";
        if (this.scannerDialog) {
          this.scannerDialog = false;
        }
        this.showToast("Verification stopped.", 'info');
      },

      verifyNextIndividual() {
        if (!this.guestVerificationEnabled) {
          this.verificationInProgress = false;
          this.verificationProgress = "";
          return;
        }
        if (this.stopVerificationFlag) {
          this.verificationInProgress = false;
          return;
        }
        for (let i = 0; i < this.verifiableGuestTypes.length; i++) {
          const guest = this.verifiableGuestTypes[i];
          if (guest.verifiedCount < guest.payment_guests) {
            this.openScannerForGuest(guest, i);
            return;
          }
        }
        this.verificationInProgress = false;
        this.verificationProgress = "";
        this.showToast("All guests verified successfully!", 'success');
      },

      handleVerification(data) {
        const { guest, guestKey, proof, docType, timestamp } = data;
        if (!this.guestVerificationEnabled) {
          this.scannerDialog = false;
          return;
        }
        const guestType = this.verifiableGuestTypes[guestKey];
        if (!guestType) {
          this.showToast('Error: Guest type not found.', 'error');
          this.scannerDialog = false;
          return;
        }
        const newCount = (guestType.verifiedCount || 0) + 1;
        this.$set(guestType, 'verifiedCount', newCount);
        this.$set(guestType, 'lastProof', proof);
        const index = newCount;
        const total = guestType.payment_guests;
        this.showToast(`${guestType.payment_person} (${index}/${total}) verified successfully!`, 'success');
        this.scannerDialog = false;

        if (this.verificationInProgress && !this.stopVerificationFlag) {
          setTimeout(() => {
            this.verifyNextIndividual();
          }, 500);
        } else {
          const remaining = this.totalVerificationsNeeded - this.totalVerified;
          if (remaining <= 0) {
            this.verificationProgress = "";
            this.showToast("All guests verified!", 'success');
          } else {
            this.verificationProgress = `${remaining} individual(s) remaining to verify.`;
          }
        }
      },

      onScannerClose() {
        if (this.verificationInProgress) {
          this.stopVerification();
        }
        if (!this.verificationInProgress) {
          this.verificationProgress = "";
        }
      },

      removeVehicle(key) {
        this.checkin.vehicles.splice(key, 1);
      },

      removeResident(key) {
        let removeResidentId = this.$store.state.ConservationFeeStore.residency_proof_check_in;
        removeResidentId.splice(key, 1);
        this.$store.commit("PROOF_RESIDENCY_CHECKIN", removeResidentId);
      },

      removeResidentDB(proof) {
        this.$store.dispatch("delete_proof_residency", proof);
      },

      postDetails: function () {
        let registrations = [];
        if (this.useDropdownVehicles) {
          if (!this.selectedRegistrations || this.selectedRegistrations.length === 0) {
            alert("Please select at least one vehicle registration.");
            return;
          }
          registrations = this.selectedRegistrations;
        } else {
          if (!this.manualRegistrations || this.manualRegistrations.trim() === "") {
            alert("Please enter vehicle registrations.");
            return;
          }
          registrations = this.manualRegistrations.split(',').map(s => s.trim()).filter(s => s);
          if (registrations.length === 0) {
            alert("Please enter at least one valid registration.");
            return;
          }
        }
        if (!this.details.name || this.details.name.trim() === "") {
          alert("Driver's Name is required.");
          return;
        }
        var data = {
          reg_plate: registrations.join(', '),
          disk: this.details.vehicle_disk,
          driver_name: this.details.name,
          phone_number: this.details.phone_number,
        };
        this.checkin.vehicles.push(data);
        this.selectedRegistrations = [];
        this.manualRegistrations = "";
        this.details.vehicle_disk = "";
        this.details.name = "";
        this.details.phone_number = "";
      },

      checkIn: function () {
        let proof = this.$store.state.ConservationFeeStore.conservation_fees_proof_checkin;
        let residency_proof = {
          residency_proof: proof,
        };
        Object.assign(this.checkin, residency_proof);
        this.$store.dispatch("post_checkin", this.checkin);
      },

      openJavaApplication() {
        this.$store.state.LoadingStore.loading = true;
        var data = {
          booking_id: this.$route.params.booking_id,
          currency_id: this.currency_id,
          session_id: localStorage.getItem("lkjhgfdsa"),
        };
        this.$store.dispatch("request_receipt", data);
      },

      getBookingProofs() {
        this.$store.dispatch("loading_true");
        this.preload_proof = true;
        let data = {
          booking_id: this.proof_booking_id.booking_id,
        };
        this.$store.dispatch("get_proof_of_residence", data);
      },

      closeBookingProofs() {
        this.booking_ref_proof = false;
      },

      confirmBookingProofsCheckin() {
        this.booking_ref_proof = false;
        let new_proofed = [];
        let not_proofed = [];
        let bookingIDProof = this.$store.state.ConservationFeeStore.residency_proof_booking_id;
        let alreadyProofed = this.singleBookingCheckIn.residency_proof;
        if (alreadyProofed != undefined) {
          for (let h = 0; h < alreadyProofed.length; h++) {
            let currentProof = alreadyProofed[h];
            if (
              currentProof.first_name != "" &&
              currentProof.last_name != "" &&
              currentProof.document_id != ""
            ) {
              let residency_fees_obj = {
                payment_person: currentProof.payment_person,
                payment_public_id: currentProof.payment_public_id,
                proof_point: currentProof.proof_point,
                first_name: currentProof.first_name,
                last_name: currentProof.last_name,
                document_id: currentProof.document_id,
                residency_proof_public_id: currentProof.residency_proof_public_id,
                verified: currentProof.verified,
              };
              new_proofed.push(residency_fees_obj);
            } else {
              not_proofed.push(currentProof);
            }
          }
          let bookingIdProofs = bookingIDProof;
          for (let r = 0; r < not_proofed.length; r++) {
            let found = false;
            let index = 0;
            for (let b = 0; b < bookingIdProofs.length; b++) {
              let proofed_id = bookingIdProofs[b].payment_public_id;
              if (not_proofed[r].payment_public_id == proofed_id) {
                found = true;
                index = b;
                break;
              }
            }
            if (found) {
              let residency_fees_obj = {
                payment_person: bookingIDProof[index].payment_person,
                payment_public_id: bookingIDProof[index].payment_public_id,
                proof_point: not_proofed[r].proof_point,
                first_name: bookingIDProof[index].first_name,
                last_name: bookingIDProof[index].last_name,
                document_id: bookingIDProof[index].document_id,
                verified: not_proofed[r].verified,
              };
              new_proofed.push(residency_fees_obj);
              bookingIdProofs.splice(index, 1);
            } else {
              new_proofed.push(not_proofed[r]);
            }
          }
          new_proofed.sort((a, b) => a.payment_person.localeCompare(b.payment_person));
          this.$store.commit("CONSERVATION_FEES_PROOF_CHECKIN", new_proofed);
        }
      },

      removeProofBookingId(key) {
        this.proofOfResidencyBookingId.splice(key, 1);
      }
    },
  };
</script>

<style scoped>
  .glassy-app {
    min-height: 100vh;
    background: #f0f2f5;
  }

  .checkin-page {
    min-height: 100vh;
    padding: 2rem 1.5rem;
    font-family: 'Inter', 'Montserrat', 'Knockout', 'Segoe UI', sans-serif;
    background: radial-gradient(ellipse at 50% 50%, rgba(116, 165, 51, 0.08) 0%, rgba(240, 242, 245, 0.85) 100%), url('https://www.olpejetaconservancy.org/wp-content/uploads/2026/02/Sundowners-5-scaled.jpg') no-repeat center center fixed;
    background-size: cover;
  }

  .checkin-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 1.5rem 2rem;
    background: rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-radius: 28px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
  }

    .checkin-container:hover {
      background: rgba(255, 255, 255, 0.10);
      box-shadow: 0 16px 56px rgba(0, 0, 0, 0.08);
    }

  .slide-in {
    animation: slideUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    opacity: 0;
    transform: translateY(40px);
  }

  .slide-in-delayed {
    animation: slideUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.15s forwards;
    opacity: 0;
    transform: translateY(30px);
  }

  @keyframes slideUp {
    0% {
      opacity: 0;
      transform: translateY(40px);
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .earth-color {
    font-family: Earthwerk, 'Knockout', 'Montserrat', sans-serif;
    color: #1e2a3a;
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.15);
  }

  .modern-panel {
    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.04);
    margin-bottom: 1.8rem;
    transition: all 0.3s ease;
  }

    .modern-panel:hover {
      background: rgba(255, 255, 255, 0.08);
      box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
    }

    .modern-panel > > > .v-expansion-panel__container {
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
    }

    .modern-panel > > > .v-expansion-panel__header {
      border: none !important;
      outline: none !important;
    }

    .modern-panel > > > .v-expansion-panel__body {
      border: none !important;
      outline: none !important;
    }

  .panel-title {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    margin: 0;
    padding: 0.4rem 0;
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.15);
  }

  .expand-icon {
    color: #74a533 !important;
  }

  .glassy-panel {
    background: rgba(255, 255, 255, 0.04) !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  .modern-select,
  .modern-autocomplete,
  .modern-input {
    background: rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-radius: 12px;
    border: 1px solid rgba(116, 165, 51, 0.08);
    transition: all 0.25s ease;
  }

    .modern-select:hover,
    .modern-autocomplete:hover,
    .modern-input:hover {
      background: rgba(255, 255, 255, 0.10);
      border-color: rgba(116, 165, 51, 0.15);
    }

    .modern-select > > > .v-input__slot,
    .modern-autocomplete > > > .v-input__slot,
    .modern-input > > > .v-input__slot {
      background: transparent !important;
      border-radius: 12px;
      padding: 0 12px;
    }

    .modern-select > > > .v-select__selections,
    .modern-autocomplete > > > .v-select__selections {
      padding: 6px 0;
    }

    .modern-select > > > .v-label,
    .modern-autocomplete > > > .v-label,
    .modern-input > > > .v-label {
      color: #2c3e2b;
      font-weight: 500;
    }

    .modern-select > > > input,
    .modern-autocomplete > > > input,
    .modern-input > > > input {
      color: #1e2f1a;
      font-weight: 500;
    }

    .modern-select:focus-within,
    .modern-autocomplete:focus-within,
    .modern-input:focus-within {
      border-color: #74a533;
      box-shadow: 0 0 0 3px rgba(116, 165, 51, 0.10);
      background: rgba(255, 255, 255, 0.12);
    }

  .readonly-input > > > .v-input__slot {
    background: rgba(200, 200, 200, 0.06) !important;
    cursor: default;
  }

  .glassy-input {
    background: rgba(255, 255, 255, 0.06) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }

    .glassy-input:hover {
      background: rgba(255, 255, 255, 0.10) !important;
    }

  .modern-switch > > > .v-input--selection-controls__ripple {
    color: #74a533;
  }

  .modern-switch > > > .v-input--switch__thumb {
    background: #74a533 !important;
  }

  .modern-switch > > > .v-input--switch__track {
    background: #a0c27a !important;
  }

  .glassy-switch > > > .v-input--switch__thumb {
    background: #74a533 !important;
    box-shadow: 0 2px 8px rgba(116, 165, 51, 0.15);
  }

  .modern-btn {
    border-radius: 60px !important;
    font-family: 'Montserrat', 'Knockout', sans-serif;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    padding: 0.8rem 2rem !important;
    border: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  }

    .modern-btn:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
    }

  .glassy-btn-primary {
    background: rgba(116, 165, 51, 0.65) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: white !important;
  }

    .glassy-btn-primary:hover {
      background: rgba(116, 165, 51, 0.80) !important;
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(116, 165, 51, 0.20);
    }

  .glassy-btn-warning {
    background: rgba(255, 152, 0, 0.60) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: white !important;
  }

    .glassy-btn-warning:hover {
      background: rgba(255, 152, 0, 0.80) !important;
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(255, 152, 0, 0.20);
    }

  .glassy-btn-info {
    background: rgba(33, 150, 243, 0.60) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: white !important;
  }

    .glassy-btn-info:hover {
      background: rgba(33, 150, 243, 0.80) !important;
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(33, 150, 243, 0.20);
    }

  .glassy-btn-danger {
    background: rgba(231, 76, 60, 0.60) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: white !important;
  }

    .glassy-btn-danger:hover {
      background: rgba(231, 76, 60, 0.80) !important;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(231, 76, 60, 0.20);
    }

  .glassy-btn-text {
    background: transparent !important;
    backdrop-filter: none;
    text-transform: none !important;
    font-weight: 600;
    color: #2c3e2b !important;
  }

    .glassy-btn-text:hover {
      background: rgba(255, 255, 255, 0.10) !important;
    }

  .glassy-btn-small {
    background: rgba(116, 165, 51, 0.55) !important;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: white !important;
    border-radius: 40px !important;
    padding: 4px 12px !important;
    font-size: 0.8rem !important;
  }

    .glassy-btn-small:hover {
      background: rgba(116, 165, 51, 0.75) !important;
    }

  .glassy-btn-complete {
    background: rgba(255, 217, 102, 0.70) !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.10);
    color: #1e2f1a !important;
    font-size: 1.2rem;
    padding: 1rem 3rem !important;
    margin-top: 2rem;
    box-shadow: 0 8px 28px rgba(255, 217, 102, 0.20);
  }

    .glassy-btn-complete:hover:not(:disabled) {
      background: rgba(255, 217, 102, 0.85) !important;
      transform: translateY(-4px);
      box-shadow: 0 12px 36px rgba(255, 217, 102, 0.30);
    }

    .glassy-btn-complete:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

  .modern-table {
    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-radius: 16px;
    border-collapse: separate;
    border-spacing: 0;
    overflow: hidden;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.03);
    width: 100%;
  }

    .modern-table th,
    .modern-table td {
      padding: 0.8rem 1.2rem;
      border: none;
      border-bottom: 1px solid rgba(116, 165, 51, 0.06);
      text-align: left;
      font-family: 'Inter', 'Montserrat', sans-serif;
      color: #1e2f1a;
    }

    .modern-table thead {
      background: rgba(116, 165, 51, 0.06);
      font-weight: 700;
      color: #1e2f1a;
      text-transform: uppercase;
      font-size: 0.85rem;
      letter-spacing: 0.5px;
    }

    .modern-table tbody tr:hover {
      background: rgba(116, 165, 51, 0.03);
    }

    .modern-table tbody tr:last-child td {
      border-bottom: none;
    }

  .glassy-table {
    background: rgba(255, 255, 255, 0.04) !important;
    border: 1px solid rgba(255, 255, 255, 0.06);
  }

    .glassy-table thead {
      background: rgba(116, 165, 51, 0.05) !important;
    }

    .glassy-table tbody tr:hover {
      background: rgba(116, 165, 51, 0.02) !important;
    }

  .section-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 2rem 0 1rem 0;
    color: #1e2f1a;
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.15);
    letter-spacing: -0.3px;
  }

  .info-section {
    margin-top: 2rem;
  }

  .modern-dialog .dialog-card {
    background: rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: 28px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  .dialog-title {
    font-size: 1.6rem;
    margin: 0;
  }

  .glassy-modal {
    background: rgba(255, 255, 255, 0.06) !important;
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.10);
  }

  .compact-summary-card {
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    transition: all 0.3s ease;
  }

    .compact-summary-card:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.10);
    }

  .summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .count-badge {
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: bold;
  }

  .glassy-badge {
    background: rgba(255, 255, 255, 0.08);
    color: #1e2f1a;
    border: 1px solid rgba(255, 255, 255, 0.04);
  }

  .doc-type-badge {
    display: flex;
    justify-content: center;
  }

  .verification-status {
    margin-top: 4px;
  }

  .proof-text {
    font-size: 0.8rem;
  }

  .glassy-chip {
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  .glassy-text {
    color: #1e2f1a;
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.15);
  }

  .glassy-text-dark {
    color: #1e2f1a;
  }

  .glassy-text-muted {
    color: #4a5568;
  }

  .glassy-progress {
    background: rgba(255, 255, 255, 0.04) !important;
  }

  .glassy-sub-card {
    background: rgba(255, 255, 255, 0.04) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 16px;
  }

  .glassy-alert {
    background: rgba(33, 150, 243, 0.06) !important;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    border: 1px solid rgba(33, 150, 243, 0.08);
    color: #1e2f1a !important;
  }

  .glassy-alert-warning {
    background: rgba(255, 152, 0, 0.06) !important;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 152, 0, 0.08);
    color: #1e2f1a !important;
  }

  @media (max-width: 968px) {
    .checkin-container {
      padding: 1rem;
    }

    .checkin-page {
      padding: 1rem 0.5rem;
    }

    .panel-title {
      font-size: 1.2rem;
    }

    .section-title {
      font-size: 1.4rem;
    }

    .modern-table th,
    .modern-table td {
      padding: 0.5rem 0.8rem;
      font-size: 0.9rem;
    }

    .modern-btn {
      padding: 0.6rem 1.2rem !important;
      font-size: 0.9rem;
    }
  }

  @media (max-width: 600px) {
    .checkin-container {
      padding: 0.6rem;
      border-radius: 16px;
    }

    .modern-table {
      font-size: 0.8rem;
    }

      .modern-table th,
      .modern-table td {
        padding: 0.4rem 0.6rem;
      }

    .section-title {
      font-size: 1.2rem;
    }

    .glassy-btn-complete {
      font-size: 1rem;
      padding: 0.8rem 1.5rem !important;
    }
  }

  @font-face {
    font-family: Earthwerk;
    font-weight: bold;
    src: url('./../../../assets/Both/Fonts/Earthwerk/PizzaDude.dk - Earthwerk.otf');
  }

  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(224, 224, 224, 0.2);
    backdrop-filter: blur(4px);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(116, 165, 51, 0.4);
    border-radius: 10px;
  }

    ::-webkit-scrollbar-thumb:hover {
      background: rgba(116, 165, 51, 0.6);
    }
</style>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,600;14..32,700;14..32,800&family=Montserrat:wght@400;500;600;700;800&display=swap');

  @font-face {
    font-family: Earthwerk;
    font-weight: bold;
    src: url('./../../../assets/Both/Fonts/Earthwerk/PizzaDude.dk - Earthwerk.otf');
  }

  .earth {
    font-family: Earthwerk;
  }
</style>